import { Box, Button } from '@wix/design-system';
import { Country, PACKAGE_PICKER_COMPONENTS, PACKAGE_PICKER_EVENTS, PaymentCycle, ProductFamily, SaleMode, packagePickerPubSub, useBeforeComponentMount } from '@wix/package-picker-common';
import { CardElevation, DeprecatedProductsCardsList, Price, ProductCardContainer, ProductCardDivider, ProductHat, ProductTitle, ShadowBehavior } from '@wix/package-picker-components';
import * as React from 'react';
import { useProducts, useQueriesHandler } from '../../hooks';
import { PackagePickerApiClient, usePackagePickerType } from '../../providers';
import { useAutonomousTranslations } from '../../providers/AutonomousTranslationsProvider';
import { AUTONOMOUS_COMPONENT_NAMES } from '../../types';
const COMPONENT_NAME = AUTONOMOUS_COMPONENT_NAMES.MARKETING_CARDS;
export const MarketingCards = _ref => {
  var _userSessionDetailsQu, _saleQuery$data, _saleQuery$data2;
  let {
    className,
    onSelectPlan,
    onInit,
    onLoad,
    onError
  } = _ref;
  useBeforeComponentMount(() => packagePickerPubSub.emit(PACKAGE_PICKER_EVENTS.COMPONENT_LOAD_START, {
    componentName: PACKAGE_PICKER_COMPONENTS.MARKETING_CARDS
  }));
  const {
    usePromotedProductFeatures,
    useRecommendedProductFamily,
    useUserSessionDetails,
    useSale
  } = PackagePickerApiClient.hooks;
  const {
    t,
    Trans
  } = useAutonomousTranslations();
  const {
    packagePickerType
  } = usePackagePickerType();
  const productsQuery = useProducts({
    packagePickerType
  });
  const {
    data: products
  } = productsQuery;
  const productIds = (products == null ? void 0 : products.reduce((filtered, product) => {
    if (product.productId) {
      filtered.push(product.productId);
    }
    return filtered;
  }, [])) ?? [];
  const userSessionDetailsQuery = useUserSessionDetails({});
  const taxIncludedLabel = t(((_userSessionDetailsQu = userSessionDetailsQuery.data) == null ? void 0 : _userSessionDetailsQu.countryCode) === Country.AU ? 'product.gst' : 'product.vat');
  const promotedProductFeaturesQuery = usePromotedProductFeatures({
    productIds,
    packagePickerType
  }, Boolean(productIds == null ? void 0 : productIds.length));
  const recommendedProductFamilyQuery = useRecommendedProductFamily({
    packagePickerType
  });
  const saleQuery = useSale({
    packagePickerType
  });
  const {
    isSuccess,
    isError,
    data
  } = useQueriesHandler({
    componentName: COMPONENT_NAME,
    callbacks: {
      onLoad,
      onError,
      onInit
    },
    queries: {
      promotedProductFeaturesQuery,
      saleQuery,
      recommendedProductFamilyQuery,
      productsQuery
    }
  });
  React.useEffect(() => {
    if (isSuccess || isError) {
      packagePickerPubSub.emit(PACKAGE_PICKER_EVENTS.COMPONENT_LOAD_FINISH, {
        componentName: PACKAGE_PICKER_COMPONENTS.MARKETING_CARDS,
        data,
        isError
      });
    }
  }, [isSuccess, isError]);
  if (!isSuccess || !recommendedProductFamilyQuery.data || !promotedProductFeaturesQuery.data) {
    return null;
  }
  const isSaleOffered = !!((_saleQuery$data = saleQuery.data) != null && _saleQuery$data.saleMode) && ((_saleQuery$data2 = saleQuery.data) == null ? void 0 : _saleQuery$data2.saleMode) !== SaleMode.None;
  const getShadow = productIndex => {
    return products != null && products[productIndex].productFamily && recommendedProductFamilyQuery.data[products == null ? void 0 : products[productIndex].productFamily] || products != null && products[productIndex + 1].productFamily && recommendedProductFamilyQuery.data[products == null ? void 0 : products[productIndex + 1].productFamily] ? ShadowBehavior.Shadow : ShadowBehavior.Border;
  };
  const maxFeaturesAmount = Math.max(...Object.values(promotedProductFeaturesQuery.data).map(features => features.length));
  return /*#__PURE__*/React.createElement(Box, {
    className: className,
    width: "100%"
  }, /*#__PURE__*/React.createElement(DeprecatedProductsCardsList, {
    height: "max-content",
    width: "100%",
    marketingItemsAmount: maxFeaturesAmount,
    background: "white"
  }, products == null ? void 0 : products.map((item, idx) => {
    var _item$pricingInfo, _item$pricingInfo2, _item$pricingInfo3;
    const {
      productId,
      name,
      description = ''
    } = item;
    if (!productId || !name) {
      return null;
    }
    const features = promotedProductFeaturesQuery.data[productId];
    const paddedFeaturesAmount = maxFeaturesAmount - features.length;
    const paddedFeatures = Array(paddedFeaturesAmount).fill({
      text: ''
    });
    const featureKeys = [...features.map(f => ({
      text: /*#__PURE__*/React.createElement(Trans, {
        i18nKey: f.name,
        components: {
          strong: /*#__PURE__*/React.createElement("b", null)
        }
      }),
      prefixIconUrl: f.iconPosition === 'prefix' ? f.iconImg : undefined,
      suffixIconUrl: f.iconPosition === 'suffix' ? f.iconImg : undefined
    })), ...paddedFeatures];
    const recommendedContent = recommendedProductFamilyQuery.data[item.productFamily];

    /* TODO: hacking solution if product don't have any YEARLY price (google-voice for example) 
    - get the dispaly cycle from props
    */
    const displayPrices = ((_item$pricingInfo = item.pricingInfo) == null || (_item$pricingInfo = _item$pricingInfo.displayPrices) == null || (_item$pricingInfo = _item$pricingInfo[PaymentCycle.YEARLY]) == null ? void 0 : _item$pricingInfo.monthly) || ((_item$pricingInfo2 = item.pricingInfo) == null || (_item$pricingInfo2 = _item$pricingInfo2.displayPrices) == null || (_item$pricingInfo2 = _item$pricingInfo2[PaymentCycle.MONTHLY]) == null ? void 0 : _item$pricingInfo2.monthly);
    const price = ((displayPrices == null ? void 0 : displayPrices.price) ?? 0) / 100;
    const fullPrice = ((displayPrices == null ? void 0 : displayPrices.fullPrice) ?? 0) / 100;
    const isProductInSale = isSaleOffered && price !== fullPrice;
    const hat = isProductInSale ? /*#__PURE__*/React.createElement(ProductHat, {
      hatText: t('premium-purchase-plan.cards.hat_sale', '50% OFF'),
      background: "#FF784B"
    }) : recommendedContent && /*#__PURE__*/React.createElement(ProductHat, {
      hatText: recommendedContent == null ? void 0 : recommendedContent.text
    });
    const isEnterprise = item.productFamily === ProductFamily.Enterprise;
    const shouldShowPrice = !!price && !isEnterprise;
    const priceDetails = {
      size: 'small',
      price,
      fullPrice,
      cycleLabel: `/${t('product_price.month')}`,
      taxIncludedLabel,
      shouldShowProductFullPrice: isProductInSale,
      shouldShowTaxIncludedLabel: !!((_item$pricingInfo3 = item.pricingInfo) != null && _item$pricingInfo3.showPriceWithTax)
    };
    const comps = [/*#__PURE__*/React.createElement(ProductCardContainer, {
      background: "white",
      pricing: shouldShowPrice && /*#__PURE__*/React.createElement(Price, priceDetails),
      key: productId,
      elevated: recommendedContent ? CardElevation.Medium : CardElevation.None,
      highlighted: !!recommendedContent,
      hat: hat,
      header: /*#__PURE__*/React.createElement(ProductTitle, {
        title: name,
        subTitle: t(description)
      }),
      cta: /*#__PURE__*/React.createElement(Button, {
        onClick: () => onSelectPlan == null ? void 0 : onSelectPlan(item),
        skin: "premium",
        priority: recommendedContent ? 'primary' : 'secondary',
        fullWidth: true
      }, t(isEnterprise ? 'premium-purchase-plan.cards.enterprise.cta' : 'productEntry.select')),
      marketingItems: featureKeys
    })];
    if (idx < products.length - 1) {
      comps.push( /*#__PURE__*/React.createElement(ProductCardDivider, {
        key: idx,
        shadowBehavior: getShadow(idx)
      }));
    }
    return comps;
  })));
};
//# sourceMappingURL=MarketingCards.js.map